import { Chart, useChart } from '@chakra-ui/charts';
import { Flex, Heading, Text } from '@chakra-ui/react';
import { getProbabilityHistory } from '@features/prediction-markets/fakeMarketData';
import { MarketOutcome, PredictionMarket } from '@features/prediction-markets/types';
import { memo, useMemo } from 'react';
import { Area, AreaChart, Tooltip, XAxis, YAxis } from 'recharts';

interface Props {
  market: PredictionMarket;
  outcome: MarketOutcome;
}

const ProbabilityChart = ({ market, outcome }: Props) => {
  const history = useMemo(() => getProbabilityHistory(market), [market]);

  const chartData = useMemo(
    () =>
      history.map((point) => ({
        date: point.label,
        probability: point.values[outcome.id] ?? outcome.probability,
      })),
    [history, outcome],
  );

  const chart = useChart({
    data: chartData,
    series: [{ name: 'probability', color: 'selection.500' }],
  });

  return (
    <Flex direction="column" gap={3} p={4}>
      <Flex alignItems="baseline" gap={2}>
        <Heading size="md" color="selection.500">
          {outcome.probability}% chance
        </Heading>
        <Text fontSize="xs" color="text.secondary">
          current
        </Text>
      </Flex>

      <Chart.Root maxH="200px" chart={chart}>
        <AreaChart data={chart.data}>
          <XAxis
            dataKey={chart.key('date')}
            tickMargin={8}
            stroke={chart.color('border.light')}
            fontSize={11}
          />
          <YAxis
            stroke={chart.color('border.light')}
            domain={[0, 100]}
            tickFormatter={(v: number) => `${v}%`}
            fontSize={11}
          />
          <Tooltip cursor={false} animationDuration={100} content={<Chart.Tooltip />} />
          {chart.series.map((item) => (
            <Area
              type="monotone"
              key={item.name}
              isAnimationActive={false}
              dataKey={chart.key(item.name)}
              fill={chart.color(item.color)}
              fillOpacity={0.15}
              stroke={chart.color(item.color)}
              strokeWidth={2}
            />
          ))}
        </AreaChart>
      </Chart.Root>
    </Flex>
  );
};

export default memo(ProbabilityChart);
